import React from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { ToastContext } from '@seccon/core/contexts/toast';
import decodeJson from '@seccon/shared/utils/decodeJson';
import { ConfirmDialogContext } from '@seccon/core/contexts/confirm';

import AssignConfirmation from '../components/AssignConfirmation';
import { ContractInfoContext } from '../contexts/contractInfo';

const useAssignPersonnel = () => {
  const { contractId } = useParams();
  const toastApi = React.useContext(ToastContext);
  const confirm = React.useContext(ConfirmDialogContext);
  const contractInfo = React.useContext(ContractInfoContext);

  /**
   * Handles assignment success by notifying the user and redirecting them back to the personnel page
   */
  function handleAssignmentSuccess () {
    // Wait for the message to be displayed before proceeding
    toastApi.success('Personnel were assigned to the contract successfully.');

    // Wait a few seconds before redirecting the user, so they can read the success message
    setTimeout(() => {
      window.location = `/contract/show/${ contractId }`;
    }, 1000);
  }

  /**
   * Handles assignment errors by notifying the user of the error that occurred
   * @param {{ body: { message: string } }} error response
   */
  function handleAssignmentError ({ body }) {
    toastApi.error(body ? `Error: ${ body.message }` : 'Unable to assign personnel to the contract. Please try again.');
  }

  /**
   * Parses the form's personnel list entry into the format that the backend expects for assignment
   * @param {object} person - form data for the personnel list entry
   * @returns {object} - backend person list entry
   */
  function parsePersonnelForAssignment (person) {
    const isAssigned = person.status?.value === 'Assigned';

    return {
      personnelId: person.personnelId,
      clearanceId: person.eligibility?.value ?? null,
      accessLevelRequired: person.accessRequired?.value ?? null,
      specialAccesses: person.specialAccesses.map((entry) => entry.value),
      status: person.status?.value ?? 'Assigned',
      dateAssigned: isAssigned ? (person.dateAssigned ?? DateTime.now()).toFormat('MM/dd/yyyy') : person.dateAssigned
    };
  }

  /**
   * Handles the API call to assign selected personnel to the indicated contract
   * @param {object[]} selectedPersonnel - list of selected personnel
   * @returns {Promise<void>} - fetch api promise
   */
  function requestAssignment (selectedPersonnel) {
    const assignPersonnel = selectedPersonnel.map(parsePersonnelForAssignment);

    return fetch(`/contractAssignment/contract/${ contractId }/assignPersonnel`, {
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      method: 'POST',
      body: JSON.stringify({ assignPersonnel })
    }).then(decodeJson);
  }

  // Wire up the assignment request, success, and error handler using react-query mutation
  const { mutateAsync } = useMutation({
    mutationFn: requestAssignment,
    onSuccess: handleAssignmentSuccess,
    onError: handleAssignmentError
  });

  /**
   * Handles assignment verification success by displaying a assignment confirmation modal to verify that the user wants to
   *  assign these personnel to this contract despite any errors indicated by the verification call.  On confirmation,
   *  this function proceeds with the assignment request.
   * @param {object[]} selectedPersonnel - list of selected personnel
   * @param {object} response - verification report API response
   * @returns {Promise<void>} - a promise that is resolved once the assignment call is complete
   */
  function handleAssignmentVerificationSuccess (selectedPersonnel, response) {
    const unapprovedCount = response?.unapprovedPersonnel?.length ?? 0;
    const approvedPersonnel = response?.approvedPersonnel ?? [];
    const unapprovedPersonnel = response?.unapprovedPersonnel ?? [];

    // If there are no unapproved personnel, then there is nothing to confirm, so move on with the mutation.
    if (unapprovedCount === 0) {
      return mutateAsync(selectedPersonnel);
    }

    // We return a promise to react-hook-form so it keeps the page in a submitting state until the mutation is complete
    return new Promise((resolve) => {
      confirm(
        'Confirm Contract Assignment',
        <AssignConfirmation
          approvedPersonnel={approvedPersonnel}
          contractInfo={contractInfo}
          selectedPersonnel={selectedPersonnel}
          unapprovedPersonnel={unapprovedPersonnel}
        />,
        // On success, run the mutation, then resolve the promise
        () => {
          mutateAsync(selectedPersonnel)
            .then(resolve)
            .catch(resolve)
        },
        // On cancel, resolve the promise right away
        () => resolve(),
        { maxWidth: 'xl' }
      )
    });
  }

  /**
   * Handles verification errors by notifying the user and proceeding with the assignment anyway.
   * @param {object[]} selectedPersonnel - list of selected personnel
   * @returns {Promise<void>} - a promise that is resolved once the assignment call is complete
   */
  function handleAssignmentVerificationError (selectedPersonnel) {
    toastApi.warning('An error occurred validating the clearance settings of the selected personnel. Regardless, proceeding with the assignment.');

    return mutateAsync(selectedPersonnel);
  }

  /**
   * Parses the form's personnel list entry into the format that the backend expects for verification
   * @param {object} person - form data for the personnel list entry
   * @returns {object} - backend person list entry
   */
  function parsePersonnelForVerification (person) {
    return {
      personnelId: person.personnelId,
      clearanceId: person.eligibility?.value ?? null,
      accessLevelRequired: person.accessRequired?.value ?? null,
      specialAccesses: person.specialAccesses.map((entry) => entry.value)
    };
  }

  /**
   * Handles the API call to verify the selected personnel's settings against the indicated contract's settings
   * @param {object[]} selectedPersonnel - list of selected personnel
   * @returns {Promise<void>} - fetch api promise
   */
  function requestAssignmentVerification (selectedPersonnel) {
    const assignPersonnel = selectedPersonnel.map(parsePersonnelForVerification);

    return fetch(`/contractAssignment/contract/${ contractId }/verifyPersonnelAssignment`, {
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      method: 'POST',
      body: JSON.stringify({ assignPersonnel })
    }).then(decodeJson)
  }

  /**
   * Form submission callback function that handles the verification and assignment of the selected personnel and their settings
   * @param {object} formData - personnel form data
   * @returns {Promise<void>} - a promise that resolves when the verification and assignment is complete.
   */
  function handleSubmit (formData) {
    const { hiddenSelectedPersonnel, selectedPersonnel: visibleSelectedPersonnel, personnel } = formData;

    // Combine the complete list of selected personnel by filtering the personnel list based on the visible selected personnel list
    //  and add in the list of hidden selected personnel
    const selectedPersonnel = personnel.filter((person) => visibleSelectedPersonnel[person.personnelId] ?? false);
    selectedPersonnel.push(...Object.values(hiddenSelectedPersonnel));

    return requestAssignmentVerification(selectedPersonnel)
      .then((response) => handleAssignmentVerificationSuccess(selectedPersonnel, response))
      .catch(() => handleAssignmentVerificationError(selectedPersonnel));
  }

  return handleSubmit;
};

export default useAssignPersonnel;
